import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Layout from "../../components/Layout";
import { get, post } from "../../services/ApiService";
import usePermissionCheck from "../../hooks/usePermissionCheck";
import { Icon, PageHeader } from "./icons";
import "./studio.css";

const TemplateList = () => {
  const canAdd = usePermissionCheck(["question.add_template"]);
  const canEdit = usePermissionCheck(["question.edit_template"]);
  const [templates, setTemplates] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const res = await get("api/assessment-templates/list");
      if (!res.success) {
        setTemplates([]);
        setError(res.error || "Could not load templates");
        return;
      }
      setTemplates(res.templates || []);
      setError("");
    } catch {
      setTemplates([]);
      setError("Could not load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter((template) => template.name.toLowerCase().includes(needle));
  }, [templates, query]);

  const remove = async (template) => {
    if (!window.confirm(`Delete “${template.name}”?`)) return;
    try {
      setDeletingId(template._id);
      const res = await post(`api/assessment-templates/delete/${template._id}`);
      if (res.success) {
        toast.success(res.message);
        setTemplates((current) => current.filter((item) => item._id !== template._id));
      } else {
        toast.error(res.error || "Could not delete template");
      }
    } catch {
      toast.error("Could not delete template");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <Layout>
      <div className="at-studio">
        <PageHeader
          kicker="Assessments"
          title="Templates"
          subtitle="Question sets assigned to products by purchase history."
        >
          {canAdd && (
            <Link className="at-btn at-btn-primary" to="/assessment-templates/new">
              <Icon name="plus" />
              New template
            </Link>
          )}
        </PageHeader>

        <section className="at-panel" aria-label="Templates">
          <div className="at-panel-head">
            <h2>
              All templates
              <span className="at-count">
                {" "}· {visible.length} {visible.length === 1 ? "template" : "templates"}
              </span>
            </h2>
            <label className="at-search">
              <Icon name="search" />
              <input
                type="search"
                value={query}
                placeholder="Search templates"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
          </div>

          {loading && <div className="at-empty">Loading templates…</div>}
          {!loading && error && <div className="at-empty">{error}</div>}
          {!loading && !error && visible.length === 0 && (
            <div className="at-empty">
              {templates.length === 0 ? "No templates yet." : "No templates match that search."}
            </div>
          )}

          {!loading && !error && visible.length > 0 && (
            <div className="at-list">
              {visible.map((template) => {
                const summary = (
                  <div className="at-row-main">
                    <strong>{template.name}</strong>
                    <span>
                      {template.questionCount} {template.questionCount === 1 ? "question" : "questions"}
                    </span>
                  </div>
                );

                return (
                  <div className="at-row" key={template._id}>
                    {canEdit ? (
                      <Link className="at-row-link" to={`/assessment-templates/${template._id}`}>
                        {summary}
                      </Link>
                    ) : (
                      summary
                    )}
                    <span className={`at-pill ${template.isActive ? "is-on" : "is-off"}`}>
                      {template.isActive ? "Active" : "Inactive"}
                    </span>
                    {canEdit && (
                      <div className="at-row-actions">
                        <button
                          type="button"
                          className="at-icon-btn is-danger"
                          aria-label={`Delete ${template.name}`}
                          disabled={deletingId === template._id}
                          onClick={() => remove(template)}
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default TemplateList;
